import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {ProgressSpinner} from 'primeng/progressspinner';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {MessageService} from 'primeng/api';
import {UserService} from '../../services/user.service';
import {AnalyticsService} from '../../services/analytics.service';

@Component({
  selector: 'homey-pay-redirect-page',
  standalone: true,
  imports: [ProgressSpinner, RouterLink],
  templateUrl: './pay-redirect-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayRedirectPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly userService = inject(UserService);
  private readonly analyticsService = inject(AnalyticsService);

  protected readonly error = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.queryParamMap.get('id');
    const user = this.userService.user();
    const isLoggedIn = user !== undefined && user.status !== 'unauthorized';

    if (!id && !isLoggedIn) {
      this.error.set('Invalid link. Please check your email or SMS for the correct link.');
      return;
    }

    void this.analyticsService.sendEvent('pay-redirect-opened', {anonymous: isLoggedIn ? 'false' : 'true'});

    try {
      const url = isLoggedIn
        ? await this.getPaymentLinkForLoggedInUser()
        : await this.getPaymentLinkForAnonymousUser(id!);

      window.location.href = url;
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) {
        this.messageService.add({
          severity: 'info',
          summary: 'No outstanding payment',
          detail: 'There is no open invoice to pay. Your payment may have already been processed.',
        });
        await this.router.navigate(['/']);
      } else {
        this.error.set('Something went wrong. Please try again or contact support.');
      }
    }
  }

  private async getPaymentLinkForLoggedInUser(): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/payment/get-payment-link`)
    );
    return response.url;
  }

  private async getPaymentLinkForAnonymousUser(id: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<{ url: string }>(`${environment.apiUrl}/api/public/payment/pay`, {params: {id}})
    );
    return response.url;
  }
}
